import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { Turn } from '../hooks/use-chat.ts';
import { AssistantMessage, UserMessage, type MessageActions } from './message.tsx';

/**
 * The message column. Follows the newest output only while the reader is
 * already at the bottom; scrolling up to reread pins the view, because a
 * reader who went back did so on purpose.
 */
export function Conversation({
  turns,
  liveTurnId,
  actions,
}: {
  turns: Turn[];
  liveTurnId: number | null;
  actions: MessageActions;
}): ReactElement {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const [isPinned, setIsPinned] = useState(true);

  useEffect(() => {
    const element = scrollRef.current;
    if (element === null || !pinnedRef.current) return;
    element.scrollTop = element.scrollHeight;
  });

  return (
    <div
      className="conversation"
      ref={scrollRef}
      onScroll={(event) => {
        const el = event.currentTarget;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        pinnedRef.current = atBottom;
        setIsPinned(atBottom);
      }}
    >
      <div className="conversation__column">
        {turns.length === 0 && (
          <div className="conversation__empty">
            <p className="conversation__empty-lede">
              Pick a scenario and run it, or ask something.
            </p>
            <p className="muted conversation__empty-note">
              The server will misbehave on purpose. The client is the part that has to hold.
            </p>
          </div>
        )}
        {turns.map((turn) =>
          turn.kind === 'user' ? (
            <UserMessage key={turn.id} turn={turn} />
          ) : (
            <AssistantMessage
              key={turn.id}
              turn={turn}
              live={turn.id === liveTurnId}
              actions={actions}
            />
          ),
        )}
      </div>
      {!isPinned && turns.length > 0 && (
        <button
          type="button"
          className="button conversation__scroll-bottom"
          onClick={() => {
            const element = scrollRef.current;
            if (element !== null) {
              element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
              pinnedRef.current = true;
              setIsPinned(true);
            }
          }}
          aria-label="Scroll to bottom"
        >
          Scroll to bottom
        </button>
      )}
    </div>
  );
}
